import React, { FC, PropsWithChildren, useState, useEffect } from "react";
import greyDialogTitleSx from './greyDialogTitleSx';
import { FormProvider, useForm } from "react-hook-form";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
// material-ui
import {
  Box,
  Grid,
  Typography,
  SelectChangeEvent,
  Button,
  Avatar,
  Select,
  Checkbox,
  MenuItem,
  ListItemText,
  FormControl,
  InputLabel,
  FormGroup,
  FormControlLabel,
  TextField,
  Chip,
  Divider,
  Dialog,
} from "@mui/material";
import CustomTextField from "../FormFields/CustomTextField";
import {
  IAccountBasicFormInputs
} from "../../utils/account.types";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { StyledInput } from './Dialog.styles';
import CustomSelect, { IOptions } from "../FormFields/CustomSelect";
import LoadingButton from "@mui/lab/LoadingButton";
import { ApiStatus } from "../../utils/api.types";
import { useAppSelector } from "../../store/hooks";

export interface IDialogProps {
  title: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  data?: any;
}

// Schema to validate account form.
const schema = yup.object().shape({
  name: yup.string().required("Name is required."),
  legalName: yup.string().required("Legal Name is required."),
  taxIdentificationNumber1: yup.string().required("Tax Identification #1 (PAN for India) is required."),
  taxIdentificationNumber2: yup.string().required("Tax Identification #2 (GST for India) is required."),
});

//Custom Dialog Component.
const CustomDialogForm: FC<PropsWithChildren<IDialogProps>> = ({
  title,
  onClose,
  open,
  children,
  onSubmit,
  data,
}) => {
  const [industryVerticalOptions, setIndustryVerticalOptions] = useState<IOptions[]>([]);
  const [selectedIndustryVertical, setSelectedIndustryVertical] = useState('');
  const { status } = useAppSelector((state) => state.account);
  const [image, setImage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [signImage, setSignImage] = useState("");
  const [selectedSignFile, setSelectedSignFile] = useState<File>();

  const [selectedBusinessType, setSelectedBusinessType] = React.useState<string[]>([]);

  const [checked, setChecked] = useState(true);
  const [logoChecked, setLogoChecked] = useState(true);
  const [advanceAccountingChecked, setAdvanceAccountingChecked] = useState(false);


  const businessTypeOptions = [
    'Product',
    'Service',
    'Consulting'
  ];

  // Populate Industry Vertical on Account form load
  useEffect(() => {
    let options = [
      {
        label: "Aerospace (aircraft manufacturing)",
        value: "Aerospace (aircraft manufacturing)",
      },
      { label: "Agriculture", value: "Agriculture" },
      { label: "Chemical (manufacturing)", value: "Chemical (manufacturing)" },
      { label: "Computers", value: "Computers" },
      { label: "Construction", value: "Construction" },
      { label: "Defense", value: "Defense" },
      {
        label: "Energy (production, distribution)",
        value: "Energy (production, distribution)",
      },
      { label: "Entertainment", value: "Entertainment" },
      { label: "Financial", value: "Financial" },
      { label: "Food", value: "Food" },
      { label: "Health Care", value: "Health Care" },
      { label: "Hospitality", value: "Hospitality" },
      { label: "Information", value: "Information" },
      { label: "Manufacturing", value: "Manufacturing" },
      { label: "Mass Media", value: "Mass Media" },
      { label: "Telecommunications", value: "Telecommunications" },
      { label: "Transport", value: "Transport" },
      { label: "Water", value: "Water" },
    ];
    setIndustryVerticalOptions(options);
  }, []);

  // use form
  const methods = useForm<IAccountBasicFormInputs>({
    resolver: yupResolver(schema)
  });

  // Method to set preview of selected picture.
  const handleSelectFile = (event: any) => {
    const target = event.target as HTMLInputElement;
    console.log(target.files);

    if (target.files && target.files[0]) {
      setSelectedFile(target.files[0]);
      setImage(URL.createObjectURL(target.files[0]));
    }
  };

  const handleSignSelectFile = (event: any) => {
    const target = event.target as HTMLInputElement;
    console.log(target.files);

    if (target.files && target.files[0]) {
      setSelectedSignFile(target.files[0]);
      setSignImage(URL.createObjectURL(target.files[0]));
    }
  };

  const innerSubmit = async (data: any) => {
    const form = new FormData();
    form.append('name', data.name);
    form.append('legalName', data.legalName);
    form.append('industryVertical', data.industryVertical);
    form.append('displayName', data.displayName);
    form.append('tagline', data.tagline || '');
    form.append('companyDesc', data.companyDesc);
    form.append('businessType', JSON.stringify(selectedBusinessType));
    form.append('taxIdentificationNumber1', data.taxIdentificationNumber1);
    form.append('taxIdentificationNumber2', data.taxIdentificationNumber2);
    form.append('website', data.website);
    form.append('contactName', data.contactName);
    form.append('email', data.email);
    form.append('whatsAppNumber', data.whatsAppNumber);

    if (checked == true) {
      form.append('showSignatureOnInvoice', 'true');
    } else {
      form.append('showSignatureOnInvoice', 'false');
    }

    if (logoChecked == true) {
      form.append('showLogoOnInvoice', 'true');
    } else {
      form.append('showLogoOnInvoice', 'false');
    }

    if (advanceAccountingChecked == true) {
      form.append('advanceAccounting', 'true');
    } else {
      form.append('advanceAccounting', 'false');
    }

    if (selectedFile) {
      form.append('logo', selectedFile);
    }
    if (selectedSignFile) {
      form.append('signature', selectedSignFile);
    }
    onSubmit(form);
  };

  const handleSelectChange = (
    event: SelectChangeEvent<typeof selectedBusinessType>
  ) => {
    const {
      target: { value },
    } = event;
    setSelectedBusinessType(
      typeof value === "string" ? value.split(",") : value
    );
  };

  useEffect(() => {
    let basicInfoAvaliable = true;

    const setInitialData = () => {
      try {
        if (data && basicInfoAvaliable) {
          methods.setValue('name', data.name);
          methods.setValue('displayName', data.displayName);
          methods.setValue('legalName', data.legalName);
          methods.setValue('tagline', data.tagline || '');
          methods.setValue('companyDesc', data.companyDesc);
          methods.setValue('website', data.website);
          methods.setValue('taxIdentificationNumber1', data.taxIdentificationNumber1);
          methods.setValue('taxIdentificationNumber2', data.taxIdentificationNumber2);
          methods.setValue('contactName', data.contactName);
          methods.setValue('email', data.email);
          methods.setValue('whatsAppNumber', data.whatsAppNumber);
          if (data.businessType) setSelectedBusinessType(data.businessType);
          setSelectedIndustryVertical(data.industryVertical);
          setImage(data.logo);
          setSignImage(data.signature);
          setLogoChecked(data.showLogoOnInvoice);
          setChecked(data.showSignatureOnInvoice);
          setAdvanceAccountingChecked(data.advanceAccounting === true || data.advanceAccounting === 'true');
        }
      } catch (error: any) {
        console.log("Dialog catch block called");
      }
    };

    setInitialData();

    return () => {
      basicInfoAvaliable = false;
    };
  }, [data, methods]);

  const inputSx = { width: '100%', mt: 0.5, borderRadius: '8px!important' };
  const selectSx = {
    width: '100%',
    mt: 0.5,
    backgroundColor: '#fff',
    borderRadius: '10px',
    padding: '8px 12px',
    '& .MuiSelect-select': { padding: '0', display: 'flex', alignItems: 'center' },
  };
  const errorText = (message?: string) => message
    ? <p style={{ color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' }}>{message}</p>
    : null;

  const textField = (name: keyof IAccountBasicFormInputs, label: string, placeholder: string, extra: any = {}) => (
    <>
      <h5 className='c-label'>{label}</h5>
      <TextField
        placeholder={placeholder}
        {...methods.register(name)}
        size='small'
        className='c-text-field-4'
        variant="standard"
        sx={inputSx}
        InputProps={{ disableUnderline: true }}
        {...extra}
      />
      {errorText((methods.formState.errors as any)[name]?.message)}
    </>
  );

  const section = (heading: string) => (
    <Grid item xs={12}>
      <Box sx={{ borderBottom: '1px solid #e4e4e4', pb: 1, mb: 0.5 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#1a1a1a' }}>{heading}</Typography>
      </Box>
    </Grid>
  );

  const uploadTile = (
    id: string,
    label: string,
    preview: string,
    onChange: (event: any) => void,
    showChecked: boolean,
    setShowChecked: (value: boolean) => void,
    showLabel: string,
  ) => (
    <Box sx={{ border: '1px solid #dcdcdc', borderRadius: '8px', p: 2, display: 'flex', alignItems: 'center', gap: 2, height: '100%' }}>
      <Box sx={{ width: 120, height: 80, flexShrink: 0, borderRadius: '6px', bgcolor: '#f5f6f8', border: '1px dashed #cfd4dc', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {preview
          ? <img src={preview} alt={label} referrerPolicy="no-referrer" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          : <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>No {label.toLowerCase()}</Typography>}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#1a1a1a', mb: 1 }}>{label}</Typography>
        <label htmlFor={id}>
          <StyledInput accept="image/*" id={id} type="file" onChange={onChange} />
          <Button variant="outlined" component="span" size="small" sx={{ textTransform: 'none' }}>
            {preview ? `Change ${label}` : `Upload ${label}`}
          </Button>
        </label>
        <FormControlLabel
          sx={{ display: 'flex', mt: 0.5 }}
          control={<Checkbox checked={!!showChecked} onChange={(event) => setShowChecked(event.target.checked)} color="primary" size="small" />}
          label={<Typography variant="body2">{showLabel}</Typography>}
        />
      </Box>
    </Box>
  );

  const handleCancel = () => {
    methods.reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { width: '100%', maxWidth: '1000px', maxHeight: '92vh' } }}
    >
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={methods.handleSubmit(innerSubmit)}
          noValidate
          sx={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}
        >
          <DialogTitle sx={greyDialogTitleSx}>
            <Typography variant="h5">{title}</Typography>
          </DialogTitle>

          <DialogContent sx={{ flex: 1, overflow: "auto", px: 3, pt: '20px !important', pb: 3 }}>
            <Grid container columnSpacing={2.5} rowSpacing={2}>
              {section('Logo & Signature')}
              <Grid item xs={12} md={6}>
                {uploadTile('logo', 'Logo', image, handleSelectFile, logoChecked, setLogoChecked, 'Show logo on invoice')}
              </Grid>
              <Grid item xs={12} md={6}>
                {uploadTile('signature', 'Signature', signImage, handleSignSelectFile, checked, setChecked, 'Show signature on invoice')}
              </Grid>

              <Grid item xs={12} sx={{ mt: 1 }} />
              {section('Business Information')}
              <Grid item xs={12} md={4}>{textField('name', 'Brand Name *', 'Brand Name')}</Grid>
              <Grid item xs={12} md={4}>{textField('displayName', 'Display Name', 'Display Name')}</Grid>
              <Grid item xs={12} md={4}>{textField('legalName', 'Legal/Business Name *', 'Legal Name')}</Grid>
              <Grid item xs={12} md={4}>{textField('tagline', 'Tagline', 'e.g. Fine Dining & Bar')}</Grid>
              <Grid item xs={12} md={4}>
                <h5 className='c-label'>Business Type</h5>
                <Select
                  multiple
                  value={selectedBusinessType}
                  onChange={handleSelectChange}
                  renderValue={(selected: any) => selected.join(", ")}
                  size='small'
                  variant='outlined'
                  sx={selectSx}
                >
                  {businessTypeOptions.map((name) => (
                    <MenuItem key={name} value={name}>
                      <Checkbox checked={selectedBusinessType.indexOf(name) > -1} size="small" />
                      <ListItemText primary={name} />
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12} md={4}>
                <h5 className='c-label'>Industry Vertical</h5>
                {industryVerticalOptions?.length &&
                  <Select
                    {...methods.register('industryVertical')}
                    value={selectedIndustryVertical && selectedIndustryVertical !== 'undefined' ? selectedIndustryVertical : ''}
                    onChange={(event: SelectChangeEvent<any>) => {
                      setSelectedIndustryVertical(event.target.value);
                    }}
                    displayEmpty
                    renderValue={(value: any) => value || <span style={{ color: '#9e9e9e' }}>Select industry</span>}
                    size='small'
                    sx={selectSx}
                  >
                    {industryVerticalOptions.map((option) =>
                      <MenuItem value={option.value} key={option.value}>{option.label}</MenuItem>
                    )}
                  </Select>
                }
              </Grid>
              <Grid item xs={12} md={8}>{textField('companyDesc', 'Company Description', 'Company Description', { multiline: true, rows: 2 })}</Grid>
              <Grid item xs={12} md={4}>{textField('website', 'Website', 'Website')}</Grid>

              <Grid item xs={12} sx={{ mt: 1 }} />
              {section('Tax Information')}
              <Grid item xs={12} md={4}>{textField('taxIdentificationNumber1', 'Tax Id #1 (PAN for India) *', 'PAN Number')}</Grid>
              <Grid item xs={12} md={4}>{textField('taxIdentificationNumber2', 'Tax Id #2 (GST for India) *', 'GST Number')}</Grid>

              <Grid item xs={12} sx={{ mt: 1 }} />
              {section('Contact Information')}
              <Grid item xs={12} md={4}>{textField('contactName', 'Contact Name', 'Contact Name')}</Grid>
              <Grid item xs={12} md={4}>{textField('email', 'Email', 'Email')}</Grid>
              <Grid item xs={12} md={4}>{textField('whatsAppNumber', 'WhatsApp Number', 'WhatsApp Number')}</Grid>

              <Grid item xs={12} sx={{ mt: 1 }} />
              {section('Account Preferences')}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={advanceAccountingChecked}
                      onChange={(event) => setAdvanceAccountingChecked(event.target.checked)}
                      name="advanceAccountingChecked"
                      color="primary"
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Enable Advance Accounting (Double Entry)</Typography>}
                />
              </Grid>
            </Grid>
            {children}
          </DialogContent>

          <DialogActions sx={{ justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              name={'submit'}
              loading={status === ApiStatus.LOADING}
              variant="contained"
            >
              Save
            </LoadingButton>
          </DialogActions>
        </Box>
      </FormProvider>
    </Dialog>
  );
};

export default CustomDialogForm;
